import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomerPageLayout from "../../customer-page-layout/CustomerPageLayout";
import { ToggleButtonGroup, ToggleOption } from "../../components/toggle-button-group";
import { Button } from "../../components/button";
import { FileInput } from "../../components/file-input";
import { AvatarModal } from "./components/avatar-modal/avatar-modal";
import { ApplicationRoutes } from "../../../../routes";
import { useUpdateUserPhotoMutation, useSetAlphabeticalUserPhotoMutation } from "../../../../service/user-service";
import useWindowDimensions from "../../../../hooks/use-window-dimensions";
import styles from "./profile-picture.module.scss";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../../utils/error-handler";

export const ProfilePicturePage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const currentScreen = useWindowDimensions();
	const [selected, setSelected] = useState<string>();
	const [imageFile, setImageFile] = useState<File | null>(null);
	const [showAvatarModal, setShowAvatarModal] = useState(false);

	const [updateUserPhoto, { isLoading: isUploadingPhoto }] = useUpdateUserPhotoMutation();
	const [setAlphabetical, { isLoading: isSettingAlphabet }] = useSetAlphabeticalUserPhotoMutation();

	const isSaving = isUploadingPhoto || isSettingAlphabet;

	const isSelectEnabled = (selected === "image" && imageFile !== null) || selected === "alphabet";

	const handleToggleChange = (value: string) => {
		setSelected(value);
		if (value === "avatar") setShowAvatarModal(true);
	};

	const handleSuccess = () => {
		setSelected(undefined);
		if (currentScreen.isMobile) {
			navigate(`/${ApplicationRoutes.SETTINGS}`);
		}
	};

	const handleSelect = async () => {
		if (selected === "image" && imageFile) {
			const formData = new FormData();
			formData.append("Image", imageFile);
			updateUserPhoto(formData)
				.unwrap()
				.then(res => {
					toast.success(t("account.web.profileImageUpdateSuccessful"));
					handleSuccess();
				})
				.catch(err => toast.error(getErrorMessage(err?.data)));
		} else if (selected === "alphabet") {
			setAlphabetical()
				.unwrap()
				.then(res => {
					toast.success(t("account.web.profileImageUpdateSuccessful"));
					handleSuccess();
				})
				.catch(err => toast.error(getErrorMessage(err?.data)));
		}
	};

	const profilePictureOptions: ToggleOption[] = [
		{
			value: "image",
			label: t("account.image"),
			expandOnSelect: true,
			customContent: <FileInput value={imageFile} onChange={setImageFile} accept="image/*" placeholder={t("account.web.noImageSelected")} />,
		},
		{ value: "avatar", label: t("account.avatar") },
		{ value: "alphabet", label: t("account.alphabet") },
	];

	return (
		<CustomerPageLayout title={t("account.editProfilePicture")}>
			<div className={styles.profilePicture}>
				<div className={styles.optionsWrapper}>
					<ToggleButtonGroup
						options={profilePictureOptions}
						value={selected}
						onChange={handleToggleChange}
						name="profile-picture-type"
						direction="vertical"
						variant="mobile"
					/>
				</div>
				<div className={styles.actions}>
					<Button variant="primary" disabled={!isSelectEnabled || isSaving} onClick={handleSelect}>
						{isSaving ? t("account.web.saving") : t("common.actions.select")}
					</Button>
				</div>
			</div>

			<AvatarModal
				isOpen={showAvatarModal}
				onClose={() => {
					setShowAvatarModal(false);
					setSelected(undefined);
				}}
				onSave={() => {
                    setShowAvatarModal(false);
                    handleSuccess();
                }}
			/>
		</CustomerPageLayout>
	);
};
